// Technicians roster — moved out of Settings → General (iter268) so every
// person in the app lives under Users. Same technicians collection, same
// fields, same ids: dispatch cards, timesheets, GPS and Miter mapping all
// keep keying off `technicians.id`.
import { useEffect, useState } from "react";
import { Plus, Edit2, Archive, X, ChevronDown, MapPin, Send } from "lucide-react";
import { btn } from "@/components/common/btn";
import { consumeFocusTarget, FOCUS_TECH } from "@/lib/deepLinkFocus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import {
  getTechnicians, createTechnician, updateTechnician, deleteTechnician,
  getMasterScopes, geocodeAddress,
} from "@/lib/api";
import { SettingsPanel, PanelHeader, LoadingState } from "@/components/settings/SettingsUI";
import { Dialog } from "@/components/ui/dialog";
import InviteFieldUserDialog from "@/components/users/InviteFieldUserDialog";

const TECH_COLORS = ["#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];

export default function TechniciansPanel() {
  const [technicians, setTechnicians] = useState([]);
  const [masterScopes, setMasterScopes] = useState([]);
  const [techForm, setTechForm] = useState(null);
  const [inviteTech, setInviteTech] = useState(null);
  // iter274 — Cmd+K deep-link: open this tech's drawer once the list loads.
  useEffect(() => {
    if (!technicians.length) return;
    const target = consumeFocusTarget(FOCUS_TECH);
    if (!target) return;
    const tech = technicians.find((t) => t.id === target);
    if (tech) setTechForm({ ...tech });
  }, [technicians]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const res = await getTechnicians({ includeArchived: true });
    setTechnicians(res.data);
  };

  useEffect(() => {
    Promise.all([getTechnicians({ includeArchived: true }), getMasterScopes()])
      .then(([t, s]) => { setTechnicians(t.data); setMasterScopes(s.data); })
      .catch(() => toast.error("Failed to load technicians"))
      .finally(() => setLoading(false));
  }, []);

  const handleSaveTech = async () => {
    if (!techForm || !techForm.name.trim()) { toast.error("Name is required"); return; }
    try {
      const payload = { ...techForm };
      if (payload.skills_text != null) {
        payload.skills = payload.skills_text.split(",").map(s => s.trim()).filter(Boolean);
        delete payload.skills_text;
      }
      if (payload.id) await updateTechnician(payload.id, payload);
      else await createTechnician(payload);
      await reload();
      setTechForm(null);
      toast.success(techForm.id ? "Technician updated" : "Technician added");
    } catch { toast.error("Failed to save technician"); }
  };

  const handleDeleteTech = async (tech) => {
    if (!window.confirm(`Archive ${tech.name}?\n\nThey drop off dispatch and the roster, but every past dispatch, timesheet and WO stays linked. Restore any time from the Archived tab.`)) return;
    try {
      await deleteTechnician(tech.id);
      setTechnicians(prev => prev.map(t => t.id === tech.id ? { ...t, is_archived: true, is_active: false } : t));
      toast.success("Technician archived — find them under Archived");
    } catch { toast.error("Failed to archive"); }
  };

  const activeTechs = technicians.filter(t => !t.is_archived);

  if (loading) return <SettingsPanel><LoadingState testId="technicians-loading" /></SettingsPanel>;

  return (
    <SettingsPanel testId="technicians-panel">
      <PanelHeader icon={Plus} title="Technicians" count={activeTechs.length} hint="Field roster · skills · scopes · GPS · Miter" testId="section-technicians"
        actions={!techForm && (
          <button type="button" className={btn("write")} onClick={() => setTechForm({ name: "", email: "", phone: "", skills_text: "", scope_ids: [], time_off: [], color: "#3b82f6", is_active: true })} data-testid="btn-add-tech">
            <Plus className="w-3.5 h-3.5" /> Tech
          </button>
        )} />
      <div className="px-5 py-4 space-y-4">
                {/* Add/Edit Tech Form */}
        {techForm ? (
          <div className="border rounded-lg p-4 bg-slate-50 space-y-3" data-testid="tech-form">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={techForm.name} onChange={(e) => setTechForm(prev => ({ ...prev, name: e.target.value }))} data-testid="input-tech-name" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={techForm.email || ""} onChange={(e) => setTechForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Optional" className="placeholder:text-slate-400" data-testid="input-tech-email" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={techForm.phone || ""} onChange={(e) => setTechForm(prev => ({ ...prev, phone: formatPhoneNumber(e.target.value) }))} placeholder="303-333-3333" className="placeholder:text-slate-400" data-testid="input-tech-phone" />
              </div>
            </div>
            {/* Lead Technician — adds the Dispatch board (full
                dispatcher) and Timesheets (view + approve the crew)
                to this person's Tech + Map access. Applied at their
                next sign-in. */}
            <label className="flex items-start gap-2 p-2.5 rounded-md border border-slate-200 bg-white cursor-pointer hover:border-slate-300">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-slate-900"
                checked={!!techForm.is_lead}
                onChange={(e) => setTechForm(prev => ({ ...prev, is_lead: e.target.checked }))}
                data-testid="checkbox-tech-is-lead"
              />
              <span className="text-xs leading-snug">
                <span className="font-semibold text-slate-700">Lead Technician</span>
                <span className="block text-slate-400">
                  Adds Dispatch (full board control) and Timesheets (view + approve the crew) on top of Tech and Map. Takes effect at their next sign-in.
                </span>
              </span>
            </label>
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Home Address</Label>
                <Input value={techForm.address || ""} onChange={(e) => setTechForm(prev => ({ ...prev, address: e.target.value }))} placeholder="Street address" className="placeholder:text-slate-400" autoComplete="off" data-testid="input-tech-address" />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input value={techForm.city || ""} onChange={(e) => setTechForm(prev => ({ ...prev, city: e.target.value }))} placeholder="City" className="placeholder:text-slate-400" autoComplete="off" data-testid="input-tech-city" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={techForm.state || ""} onChange={(e) => setTechForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))} placeholder="ST" className="placeholder:text-slate-400" autoComplete="off" data-testid="input-tech-state" />
              </div>
              <div>
                <Label className="text-xs">Zip Code</Label>
                <Input value={techForm.zip_code || ""} onChange={(e) => setTechForm(prev => ({ ...prev, zip_code: e.target.value }))} placeholder="80021" className="placeholder:text-slate-400" autoComplete="off" data-testid="input-tech-zip" />
              </div>
            </div>
            {/* Home geofence + weekly hours budget — all live in one
                row so the tech editor stays compact. Geocode derives
                home_lat/home_lng from the address fields above. */}
            <div className="grid grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Home Lat</Label>
                <Input type="number" step="0.000001"
                  value={techForm.home_lat ?? ""}
                  onChange={e => setTechForm(prev => ({ ...prev, home_lat: parseFloat(e.target.value) || null }))}
                  className="h-9 tabular-nums" data-testid="input-tech-home-lat" />
              </div>
              <div>
                <Label className="text-xs">Home Lng</Label>
                <Input type="number" step="0.000001"
                  value={techForm.home_lng ?? ""}
                  onChange={e => setTechForm(prev => ({ ...prev, home_lng: parseFloat(e.target.value) || null }))}
                  className="h-9 tabular-nums" data-testid="input-tech-home-lng" />
              </div>
              <div>
                <Label className="text-xs">Home Geofence (ft)</Label>
                <Input type="number" step="50" min="100"
                  value={techForm.home_geofence_radius_ft ?? 500}
                  onChange={e => setTechForm(prev => ({ ...prev, home_geofence_radius_ft: parseInt(e.target.value) || 500 }))}
                  className="h-9 tabular-nums" data-testid="input-tech-home-radius" />
              </div>
              <div>
                <Label className="text-xs">Weekly Budget (hrs)</Label>
                <Input type="number" step="1" min="0"
                  value={techForm.weekly_hours_budget ?? 40}
                  onChange={e => setTechForm(prev => ({ ...prev, weekly_hours_budget: parseInt(e.target.value) || 40 }))}
                  className="h-9 tabular-nums" data-testid="input-tech-weekly-budget" />
              </div>
              <div>
                {/* Miter Employee ID lives in the Miter Payroll
                    section below alongside Department / Default
                    Job / Timezone — kept together since they all
                    flow into the weekly payroll push. */}
              </div>
              <div className="flex items-end">
                <Button variant="outline" className="h-9 w-full gap-1" data-testid="btn-tech-geocode"
                  onClick={async () => {
                    const full = `${techForm.address || ""}, ${techForm.city || ""}, ${techForm.state || ""} ${techForm.zip_code || ""}`.trim();
                    if (full.replace(/[ ,]/g, "").length === 0) { toast.error("Enter home address first"); return; }
                    try {
                      const res = await geocodeAddress({ address: full });
                      setTechForm(prev => ({ ...prev, home_lat: res.data.lat, home_lng: res.data.lng }));
                      toast.success("Home geocoded");
                    } catch { toast.error("Address not found"); }
                  }}>
                  <MapPin className="w-3.5 h-3.5" />Geocode Home
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Scopes (Skills)</Label>
                <div className="flex flex-wrap gap-1.5 mt-1 max-h-28 overflow-y-auto" data-testid="input-tech-scopes">
                  {masterScopes.map(scope => {
                    const selected = (techForm.scope_ids || []).includes(scope.id);
                    return (
                      <button
                        key={scope.id}
                        type="button"
                        onClick={() => setTechForm(prev => {
                          const ids = prev.scope_ids || [];
                          return { ...prev, scope_ids: selected ? ids.filter(id => id !== scope.id) : [...ids, scope.id] };
                        })}
                        className={`text-[11px] px-2 py-1 rounded-full border transition-all ${selected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
                      >
                        {scope.name}
                      </button>
                    );
                  })}
                  {masterScopes.length === 0 && <span className="text-xs text-slate-400">No scopes configured in Equipment Library</span>}
                </div>
              </div>
              <div>
                <Label className="text-xs">Calendar Color</Label>
                <div className="flex gap-1.5 mt-1">
                  {TECH_COLORS.map(c => (
                    <button key={c} className={`w-7 h-7 rounded-full border-2 ${techForm.color === c ? "border-slate-900 scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: c }} onClick={() => setTechForm(prev => ({ ...prev, color: c }))} />
                  ))}
                </div>
              </div>
            </div>
            {/* Teammate */}
            <div>
              <Label className="text-xs">Teammate</Label>
              <Select value={techForm.teammate_id || "__none__"} onValueChange={(v) => setTechForm(prev => ({ ...prev, teammate_id: v === "__none__" ? null : v }))}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-teammate">
                  <SelectValue placeholder="Select teammate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No Teammate</SelectItem>
                  {technicians.filter(t => t.id !== techForm.id).map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color || "#3b82f6" }} />
                        {t.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Time Off */}
            <div>
              <Label className="text-xs">Time Off</Label>
              <div className="space-y-1.5 mt-1">
                {(techForm.time_off || []).map((to, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- editable time-off ranges; arr[i] is the write target
                  <div key={`to-${i}`} className="flex items-center gap-2 text-xs">
                    <input type="date" value={to.start_date || ""} onChange={(e) => {
                      const arr = [...(techForm.time_off || [])];
                      arr[i] = { ...arr[i], start_date: e.target.value };
                      setTechForm(prev => ({ ...prev, time_off: arr }));
                    }} className="border rounded px-2 py-1 text-xs" />
                    <span className="text-slate-400">to</span>
                    <input type="date" value={to.end_date || ""} onChange={(e) => {
                      const arr = [...(techForm.time_off || [])];
                      arr[i] = { ...arr[i], end_date: e.target.value };
                      setTechForm(prev => ({ ...prev, time_off: arr }));
                    }} className="border rounded px-2 py-1 text-xs" />
                    <input value={to.reason || ""} onChange={(e) => {
                      const arr = [...(techForm.time_off || [])];
                      arr[i] = { ...arr[i], reason: e.target.value };
                      setTechForm(prev => ({ ...prev, time_off: arr }));
                    }} placeholder="Reason (Vacation, Sick...)" className="border rounded px-2 py-1 text-xs flex-1 placeholder:text-slate-300" />
                    <button onClick={() => {
                      const arr = (techForm.time_off || []).filter((_, j) => j !== i);
                      setTechForm(prev => ({ ...prev, time_off: arr }));
                    }} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setTechForm(prev => ({ ...prev, time_off: [...(prev.time_off || []), { start_date: "", end_date: "", reason: "" }] }))} data-testid="btn-add-time-off">
                  <Plus className="w-3 h-3 mr-1" />Add Time Off
                </Button>
              </div>
            </div>
            {/* Miter Payroll — collapsible. All 4 fields populate
                directly into Miter timesheets on the weekly push.
                Changes here never trigger a push; the manager-only
                "/timesheets" tab does that. */}
            <details className="border border-slate-200 rounded-lg" data-testid="miter-payroll-section">
              <summary className="px-3 py-2 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-50 flex items-center justify-between">
                <span>Miter Payroll</span>
                <span className="text-[10px] text-slate-400 font-normal">
                  {(techForm.miter_employee_id ? "✓ ID" : "—")}{" · "}
                  {(techForm.miter_department ? "✓ Dept" : "—")}
                </span>
              </summary>
              <div className="p-3 space-y-2 border-t border-slate-200">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Miter Employee ID</Label>
                    <Input value={techForm.miter_employee_id || ""} onChange={(e) => setTechForm(prev => ({ ...prev, miter_employee_id: e.target.value }))} placeholder="tm_..." className="h-8 text-xs mt-1" data-testid="input-miter-employee-id" />
                  </div>
                  <div>
                    <Label className="text-xs">Department</Label>
                    <Select value={techForm.miter_department || "__unset__"} onValueChange={(v) => setTechForm(prev => ({ ...prev, miter_department: v === "__unset__" ? "" : v }))}>
                      <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-miter-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unset__">— Not set —</SelectItem>
                        <SelectItem value="Service">Service</SelectItem>
                        <SelectItem value="Service Tech">Service Tech</SelectItem>
                        <SelectItem value="Installers">Installers</SelectItem>
                        <SelectItem value="Field Operations">Field Operations</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Default Job (no-WO days)</Label>
                    <Input value={techForm.miter_default_job || ""} onChange={(e) => setTechForm(prev => ({ ...prev, miter_default_job: e.target.value }))} placeholder="9998 ABS Internal" className="h-8 text-xs mt-1" data-testid="input-miter-default-job" />
                  </div>
                  <div>
                    <Label className="text-xs">Timezone</Label>
                    <Select value={techForm.miter_timezone || "America/Denver"} onValueChange={(v) => setTechForm(prev => ({ ...prev, miter_timezone: v }))}>
                      <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-miter-timezone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/Denver">America/Denver (MST/MDT)</SelectItem>
                        <SelectItem value="America/Phoenix">America/Phoenix (MST)</SelectItem>
                        <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</SelectItem>
                        <SelectItem value="America/Chicago">America/Chicago (CST/CDT)</SelectItem>
                        <SelectItem value="America/New_York">America/New_York (EST/EDT)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </details>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveTech} data-testid="btn-save-tech">{techForm.id ? "Update" : "Add"} Technician</Button>
              <Button variant="outline" size="sm" onClick={() => setTechForm(null)}><X className="w-3 h-3 mr-1" />Cancel</Button>
            </div>
          </div>
        ) : null}

        {/* Tech List */}
        {activeTechs.length > 0 ? (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="w-8"></TableHead>
              <TableHead className="text-xs font-semibold">Name</TableHead>
              <TableHead className="text-xs font-semibold">Email</TableHead>
              <TableHead className="text-xs font-semibold whitespace-nowrap w-[120px]">Phone</TableHead>
              <TableHead className="text-xs font-semibold">Scopes</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {activeTechs.map(tech => (
                <TableRow key={tech.id} data-testid={`tech-row-${tech.id}`}>
                  <TableCell><div className="w-4 h-4 rounded-full" style={{ backgroundColor: tech.color || "#3b82f6" }} /></TableCell>
                  <TableCell className="text-sm font-medium">
                    {tech.name}
                    {tech.is_lead && (
                      <span className="ml-2 align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-900 text-white" data-testid={`tech-lead-badge-${tech.id}`}>LEAD</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{tech.email || "-"}</TableCell>
                  {/* Phone — locked to a single-line display so the
                      "303-961-0056" format never wraps into two rows.
                      Fixed width keeps the column narrow so Scopes
                      gets the slack. */}
                  <TableCell className="text-sm text-slate-500 whitespace-nowrap tabular-nums font-mono w-[120px]">{tech.phone ? formatPhoneNumber(tech.phone) : "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[260px] truncate" title={(tech.scope_ids || []).map(sid => masterScopes.find(s => s.id === sid)?.name || sid).join(", ")}>
                    {(tech.scope_ids || []).length > 0
                      ? (tech.scope_ids || []).map(sid => masterScopes.find(s => s.id === sid)?.name || sid).join(", ")
                      : (tech.skills || []).join(", ") || "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {/* iter300 — invite this tech to sign in on their
                          phone without hopping to the Field access tab.
                          Same dialog, same endpoint; needs an email
                          because the roster link matches on it. */}
                      <Button
                        variant="ghost" size="sm"
                        className="h-6 w-6 p-0 text-sky-600 hover:text-sky-700 disabled:text-slate-300"
                        onClick={() => setInviteTech(tech)}
                        disabled={!(tech.email || "").trim()}
                        title={(tech.email || "").trim()
                          ? `Send ${tech.name} a magic-link sign-in invite`
                          : "Add an email to this technician first — the invite is sent to it"}
                        data-testid={`btn-invite-tech-${tech.id}`}
                      ><Send className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setTechForm({ ...tech, skills_text: (tech.skills || []).join(", "), scope_ids: tech.scope_ids || [], time_off: tech.time_off || [] })} data-testid={`btn-edit-tech-${tech.id}`}><Edit2 className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-amber-500" onClick={() => handleDeleteTech(tech)} title="Archive — restore from the Archived tab" data-testid={`btn-archive-tech-${tech.id}`}><Archive className="w-3 h-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-4 text-slate-400 text-sm">{technicians.length ? "No active technicians — archived ones are under the Archived tab" : "No technicians added yet"}</div>
        )}
      </div>

      {inviteTech && (
        <Dialog open onOpenChange={() => setInviteTech(null)}>
          <InviteFieldUserDialog
            prefill={{ name: inviteTech.name, email: inviteTech.email, phone: inviteTech.phone }}
            onDone={() => setInviteTech(null)}
            onCancel={() => setInviteTech(null)}
          />
        </Dialog>
      )}
    </SettingsPanel>
  );
}
